// src/store/slices/autoPilotSlice.js
import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Session-based state (new API)
  currentSessionId: null,
  currentSession: null,
  sessions: [],
  pagination: null,
  
  // Step-based state (legacy, kept for backward compatibility)
  currentStep: 1, // 1: Intent, 2: ICP Validation, 3: Search Progress, 4: Results Review, 5: List Creation
  userIntent: '',
  generatedICP: null,
  validatedICP: null,
  searchQueries: [],
  searchResults: [],
  filteredProspects: [],
  enrichedProspects: [],
  verifiedProspects: [],
  selectedProspects: [],
  listData: null,
  slackWebhook: '',
  loading: false,
  error: null,
  searchProgress: {
    totalQueries: 0,
    completedQueries: 0,
    resultsPerQuery: {}
  },
  enrichmentStatus: {
    total: 0,
    completed: 0,
    failed: 0
  }
};

const autoPilotSlice = createSlice({
  name: 'autoPilot',
  initialState,
  reducers: {
    setCurrentStep(state, action) {
      state.currentStep = action.payload;
    },
    setUserIntent(state, action) {
      state.userIntent = action.payload;
    },
    setGeneratedICP(state, action) {
      state.generatedICP = action.payload;
    },
    setValidatedICP(state, action) {
      state.validatedICP = action.payload;
    },
    setSearchQueries(state, action) {
      state.searchQueries = action.payload;
      state.searchProgress.totalQueries = action.payload.length;
    },
    setSearchResults(state, action) {
      state.searchResults = action.payload;
    },
    setFilteredProspects(state, action) {
      state.filteredProspects = action.payload;
    },
    setEnrichedProspects(state, action) {
      state.enrichedProspects = action.payload;
    },
    setVerifiedProspects(state, action) {
      state.verifiedProspects = action.payload;
      // Auto-select up to 20 prospects
      state.selectedProspects = action.payload.slice(0, 20);
    },
    toggleProspectSelection(state, action) {
      const prospectId = action.payload;
      const index = state.selectedProspects.findIndex(p => p._id === prospectId || p.id === prospectId);
      if (index >= 0) {
        state.selectedProspects.splice(index, 1);
      } else {
        const prospect = state.verifiedProspects.find(p => p._id === prospectId || p.id === prospectId);
        if (prospect && state.selectedProspects.length < 20) {
          state.selectedProspects.push(prospect);
        }
      }
    },
    setListData(state, action) {
      state.listData = action.payload;
    },
    setSlackWebhook(state, action) {
      state.slackWebhook = action.payload;
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
    },
    updateSearchProgress(state, action) {
      const { queryId, results, completed } = action.payload;
      state.searchProgress.completedQueries = completed;
      if (queryId) {
        state.searchProgress.resultsPerQuery[queryId] = results;
      }
    },
    updateEnrichmentStatus(state, action) {
      state.enrichmentStatus = {
        ...state.enrichmentStatus,
        ...action.payload
      };
    },
    resetAutoPilot(state) {
      return initialState;
    },
    goToNextStep(state) {
      if (state.currentStep < 5) {
        state.currentStep += 1;
      }
    },
    goToPreviousStep(state) {
      if (state.currentStep > 1) {
        state.currentStep -= 1;
      }
    },
    // Session-based actions (new API)
    setCurrentSessionId(state, action) {
      state.currentSessionId = action.payload;
    },
    setCurrentSession(state, action) {
      state.currentSession = action.payload;
      // Sync legacy state from session for backward compatibility
      if (action.payload) {
        state.userIntent = action.payload.intent || state.userIntent;
        if (action.payload.icpId && typeof action.payload.icpId === 'object') {
          state.generatedICP = action.payload.icpId;
        }
        if (action.payload.progress) {
          // Map session progress to step-based progress
          const progress = action.payload.progress;
          if (progress.percentage <= 20) {
            state.currentStep = 1;
          } else if (progress.percentage <= 40) {
            state.currentStep = 2;
          } else if (progress.percentage <= 60) {
            state.currentStep = 3;
          } else if (progress.percentage <= 80) {
            state.currentStep = 4;
          } else {
            state.currentStep = 5;
          }
        }
        if (action.payload.listId && typeof action.payload.listId === 'object') {
          state.listData = action.payload.listId;
        }
      }
    },
    setSessions(state, action) {
      state.sessions = action.payload.sessions || action.payload;
    },
    setPagination(state, action) {
      state.pagination = action.payload;
    },
    updateSessionStatus(state, action) {
      if (state.currentSession && state.currentSession._id === action.payload._id) {
        state.currentSession = { ...state.currentSession, ...action.payload };
      }
      // Update in sessions array if present
      const sessionIndex = state.sessions.findIndex(s => s._id === action.payload._id);
      if (sessionIndex >= 0) {
        state.sessions[sessionIndex] = { ...state.sessions[sessionIndex], ...action.payload };
      }
    }
  }
});

export const {
  setCurrentStep,
  setUserIntent,
  setGeneratedICP,
  setValidatedICP,
  setSearchQueries,
  setSearchResults,
  setFilteredProspects,
  setEnrichedProspects,
  setVerifiedProspects,
  toggleProspectSelection,
  setListData,
  setSlackWebhook,
  setLoading,
  setError,
  updateSearchProgress,
  updateEnrichmentStatus,
  resetAutoPilot,
  goToNextStep,
  goToPreviousStep,
  // Session-based actions
  setCurrentSessionId,
  setCurrentSession,
  setSessions,
  setPagination,
  updateSessionStatus
} = autoPilotSlice.actions;

export default autoPilotSlice.reducer;
